import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserIcon, LogOutIcon, ShieldIcon, ChevronDownIcon, MenuIcon, XIcon } from "../ui/Icons";
import { Logo } from "../ui/Logo";
import { useAuth } from "../../hooks/useAuth";

export function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <header className="fixed left-0 right-0 top-0 z-40 border-b border-elevated/50 bg-base/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo + Navigation */}
          <div className="flex items-center gap-8">
            <Logo />

            <nav className="hidden items-center gap-1 md:flex">
              <a
                href="/#plugins"
                className="rounded-lg px-4 py-2 font-body text-sm text-muted transition-colors hover:bg-elevated/50 hover:text-secondary"
              >
                Explore
              </a>
              <a
                href="/#marketplaces"
                className="rounded-lg px-4 py-2 font-body text-sm text-muted transition-colors hover:bg-elevated/50 hover:text-secondary"
              >
                Marketplaces
              </a>
              <Link
                to="/create"
                className={`rounded-lg px-4 py-2 font-body text-sm transition-colors ${
                  isActive("/create")
                    ? "bg-elevated text-primary"
                    : "text-muted hover:bg-elevated/50 hover:text-secondary"
                }`}
              >
                Create
              </Link>
              <Link
                to="/docs"
                className={`rounded-lg px-4 py-2 font-body text-sm transition-colors ${
                  isActive("/docs")
                    ? "bg-elevated text-primary"
                    : "text-muted hover:bg-elevated/50 hover:text-secondary"
                }`}
              >
                Documentation
              </Link>
            </nav>
          </div>

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-elevated/50"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber to-amber-hover">
                    <UserIcon size={16} className="text-black" />
                  </div>
                  <span className="hidden font-mono text-sm text-secondary sm:inline">
                    {user?.name || user?.email}
                  </span>
                  <ChevronDownIcon
                    size={14}
                    className={`hidden text-muted transition-transform sm:block ${
                      dropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 min-w-[240px] overflow-hidden rounded-lg border border-elevated bg-surface shadow-xl">
                    <div className="border-b border-elevated px-4 py-3">
                      <p className="font-mono text-sm text-primary">{user?.name}</p>
                      <p className="truncate font-mono text-xs text-muted">{user?.email}</p>
                    </div>
                    <div className="p-1">
                      <Link
                        to="/dashboard"
                        onClick={() => setDropdownOpen(false)}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-secondary transition-colors hover:bg-elevated"
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/dashboard/marketplaces"
                        onClick={() => setDropdownOpen(false)}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-secondary transition-colors hover:bg-elevated"
                      >
                        My Marketplaces
                      </Link>
                      <Link
                        to="/dashboard/purchases"
                        onClick={() => setDropdownOpen(false)}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-secondary transition-colors hover:bg-elevated"
                      >
                        Purchases
                      </Link>
                      <Link
                        to="/dashboard/earnings"
                        onClick={() => setDropdownOpen(false)}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-secondary transition-colors hover:bg-elevated"
                      >
                        Earnings
                      </Link>
                      <Link
                        to="/settings/payments"
                        onClick={() => setDropdownOpen(false)}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-secondary transition-colors hover:bg-elevated"
                      >
                        Payment Settings
                      </Link>
                      {user?.isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setDropdownOpen(false)}
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-amber transition-colors hover:bg-elevated"
                        >
                          <ShieldIcon size={14} />
                          Admin
                        </Link>
                      )}
                    </div>
                    <div className="border-t border-elevated p-1">
                      <button
                        onClick={() => {
                          logout();
                          setDropdownOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-danger transition-colors hover:bg-danger/10"
                      >
                        <LogOutIcon size={14} />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden items-center gap-2 sm:flex">
                <Link
                  to="/login"
                  className="rounded-lg px-4 py-2 font-body text-sm text-muted transition-colors hover:text-secondary"
                >
                  Sign in
                </Link>
                <Link to="/signup" className="btn-primary py-2 text-sm">
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex items-center justify-center rounded-lg p-2 text-muted transition-colors hover:bg-elevated/50 hover:text-secondary md:hidden"
            >
              {mobileMenuOpen ? <XIcon size={24} /> : <MenuIcon size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-base/80 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Menu Content */}
          <div className="absolute left-0 right-0 top-16 border-b border-elevated bg-surface/95 backdrop-blur-xl">
            <nav className="flex flex-col p-4">
              <a
                href="/#plugins"
                className="rounded-lg px-4 py-3 font-body text-base text-secondary transition-colors hover:bg-elevated/50"
              >
                Explore
              </a>
              <a
                href="/#marketplaces"
                className="rounded-lg px-4 py-3 font-body text-base text-secondary transition-colors hover:bg-elevated/50"
              >
                Marketplaces
              </a>
              <Link
                to="/create"
                className={`rounded-lg px-4 py-3 font-body text-base transition-colors ${
                  isActive("/create")
                    ? "bg-elevated text-primary"
                    : "text-secondary hover:bg-elevated/50"
                }`}
              >
                Create
              </Link>
              <Link
                to="/docs"
                className={`rounded-lg px-4 py-3 font-body text-base transition-colors ${
                  isActive("/docs")
                    ? "bg-elevated text-primary"
                    : "text-secondary hover:bg-elevated/50"
                }`}
              >
                Documentation
              </Link>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className={`rounded-lg px-4 py-3 font-body text-base transition-colors ${
                      isActive("/dashboard")
                        ? "bg-elevated text-primary"
                        : "text-secondary hover:bg-elevated/50"
                    }`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/dashboard/marketplaces"
                    className={`rounded-lg px-4 py-3 font-body text-base transition-colors ${
                      isActive("/dashboard/marketplaces")
                        ? "bg-elevated text-primary"
                        : "text-secondary hover:bg-elevated/50"
                    }`}
                  >
                    My Marketplaces
                  </Link>
                  {user?.isAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-2 rounded-lg px-4 py-3 font-body text-base text-amber transition-colors hover:bg-elevated/50"
                    >
                      <ShieldIcon size={16} />
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="mt-2 flex items-center gap-2 rounded-lg border-t border-elevated px-4 py-3 font-body text-base text-danger transition-colors hover:bg-danger/10"
                  >
                    <LogOutIcon size={16} />
                    Sign out
                  </button>
                </>
              ) : (
                <div className="mt-4 flex flex-col gap-2 border-t border-elevated pt-4">
                  <Link
                    to="/login"
                    className="rounded-lg px-4 py-3 text-center font-body text-base text-secondary transition-colors hover:bg-elevated/50"
                  >
                    Sign in
                  </Link>
                  <Link to="/signup" className="btn-primary py-3 text-center text-base">
                    Get Started
                  </Link>
                </div>
              )}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
